/**
 * Catalog Tools
 *
 * AI agent tools for the user's catalog: folders of the active team to list and
 * browse, trace-viewer's shared search and placement, and save-as-asset.
 */

import type { ToolDefinition } from './types';
import { serverTool } from 'trace-viewer/cms';
import { API_BASE_URL, ensureFreshToken } from '../../catalog-auth';
import { sharedSceneTool } from './shared-scene-tools';

import type { ToolContext } from '../agent-helpers';
import { resolveUniqueByName } from '../agent-helpers';
import { catalogIndex } from '../CatalogIndex';
import { getCatalogSelectedFolderId, getCatalogSelectedFolderTitle } from '../../catalog-shared';
import { saveSelectionAsAsset } from '../../cms/save-as-asset-action';
import { PromotionRefusedError } from '../../cms/save-as-asset';
import { getActiveSelection } from '../../active-selection';
import { formatManifestOffenders } from 'trace-viewer/editor';
import type { SceneObject } from 'trace-viewer/scene';

// ── list_folders ──────────────────────────────────────────────

async function executeListFolders(
    _input: Record<string, unknown>,
    _ctx: ToolContext,
): Promise<string> {
    try {
        await catalogIndex.ensureLoaded();
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return `Error: ${msg}`;
    }

    const folders = catalogIndex.getFolders();
    if (folders.length === 0) {
        return 'No folders found for the active team.';
    }

    const lines = folders.map((f, i) => {
        const marker = f.isOpen ? ' ← open' : '';
        const defaultTag = f.isDefault ? ' (default)' : '';
        return `${i + 1}. "${f.title}"${defaultTag} — ${f.assetCount} asset(s) (id: ${f.folderId})${marker}`;
    });

    const openInfo = getCatalogSelectedFolderId()
        ? `Currently open folder: "${getCatalogSelectedFolderTitle()}"`
        : 'No folder is currently open in the catalog panel.';

    return `${openInfo}\n\nFolders:\n${lines.join('\n')}\n\nUse browse_folder to see assets in a folder.`;
}

// ── browse_folder ─────────────────────────────────────────────

async function executeBrowseFolder(
    input: Record<string, unknown>,
    _ctx: ToolContext,
): Promise<string> {
    const folderId = input.folder_id as string | undefined;

    try {
        await catalogIndex.ensureLoaded();
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return `Error: ${msg}`;
    }

    const targetId = folderId ?? getCatalogSelectedFolderId();
    if (!targetId) {
        return 'Error: No folder specified and no folder is currently open. Use list_folders to see available folders, or open a folder in the Catalog panel.';
    }

    const folders = catalogIndex.getFolders();
    const folder = folders.find(f => f.folderId === targetId);
    const folderName = folder?.title ?? 'Unknown folder';

    const assets = catalogIndex.getByFolder(targetId);
    if (assets.length === 0) {
        return `Folder "${folderName}" is empty.`;
    }

    const lines = assets.map((a, i) =>
        `${i + 1}. "${a.title}" (id: ${a.id}) — ${a.entityType ?? 'unknown type'}`,
    );
    return `Folder "${folderName}" — ${assets.length} asset(s):\n${lines.join('\n')}\n\nUse import_asset with an asset id to import into the scene.`;
}

// ── search_catalog ────────────────────────────────────────────

/**
 * trace-viewer's `search_catalog`, the one the gateway runs for Studio, run
 * here with this editor's catalog token: both editors search one catalog.
 */
const searchCatalog = serverTool('search_catalog')!;
const searchCatalogTool: ToolDefinition = {
    tool: {
        name: searchCatalog.name,
        input_schema: searchCatalog.inputSchema as unknown as ToolDefinition['tool']['input_schema'],
    },
    execute: async (input) => {
        const token = await ensureFreshToken();
        if (!token) return 'Error: Not logged into catalog. Please sign in via the Catalog panel first.';
        const outcome = await searchCatalog.run(input, { cms: { baseUrl: API_BASE_URL, token }, callerId: 'sandbox' });
        return outcome.isError ? `Error: ${outcome.text}` : outcome.text;
    },
};

// ── save_as_asset ─────────────────────────────────────────────

/**
 * The agent's half of "Save as asset" (CONTENT_MODEL_SPEC §3.5).
 *
 * Same verb as the viewport context menu, same module behind it: promote →
 * `POST /assets` → replace the promoted objects with one reference. What the
 * agent gets extra is the *reason* when a promotion is refused — the closure
 * rule's offending `files[]` entries, so it can fix the scene (copy the
 * offending reference into this scene first, say) instead of retrying blindly.
 */
async function executeSaveAsAsset(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const viewer = ctx.viewer;
    if (!viewer) {
        return 'Error: save_as_asset needs the editor (no trace-viewer element in this context).';
    }

    const name = typeof input.name === 'string' ? input.name.trim() : '';
    if (!name) return 'Error: name is required — it becomes the asset\'s title in the library.';

    const rawNames = input.object_names;
    let roots: SceneObject[];
    if (Array.isArray(rawNames) && rawNames.length > 0) {
        try {
            roots = rawNames.map(entry => resolveUniqueByName(ctx.traceScene, String(entry), 'save_as_asset'));
        } catch (err) {
            return `Error: ${err instanceof Error ? err.message : String(err)}`;
        }
    } else {
        // Default to the selection, the way the context menu does.
        const selected = getActiveSelection();
        roots = selected.length > 0 ? selected : (ctx.selectedObject ? [ctx.selectedObject] : []);
        if (roots.length === 0) {
            return 'Error: nothing selected — pass object_names, or select the objects first.';
        }
    }

    const replaceWithReference = input.replace_with_reference !== false;

    try {
        const result = await saveSelectionAsAsset(viewer, roots, { name, replaceWithReference });
        ctx.onSceneChanged?.();
        const placement = result.reference
            ? `The ${roots.length} promoted object(s) were replaced by one reference node "${result.reference.name}" `
              + `(src: ${result.referenceUrl}), which is now selected.`
            : 'The objects were left inline in the scene; nothing was replaced.';
        return `Saved "${result.name}" to the library as asset ${result.assetId} `
            + `(${result.fileCount} file(s) travelled with the document). ${placement}`;
    } catch (err) {
        if (err instanceof PromotionRefusedError) {
            return `Error: this selection cannot become a library asset — an asset may not reference a scene's own `
                + `files (CONTENT_MODEL_SPEC §3.2). Offending entries:\n${formatManifestOffenders(err.offenders)}`;
        }
        return `Error saving as asset: ${err instanceof Error ? err.message : String(err)}`;
    }
}

// ── Exports ───────────────────────────────────────────────────

export const catalogTools: ToolDefinition[] = [
    {
        tool: {
            name: 'list_folders',
            input_schema: {
                type: 'object' as const,
                properties: {},
                required: [],
            },
        },
        execute: executeListFolders,
    },
    {
        tool: {
            name: 'browse_folder',
            input_schema: {
                type: 'object' as const,
                properties: {
                    folder_id: { type: 'string' },
                },
                required: [],
            },
        },
        execute: executeBrowseFolder,
    },
    searchCatalogTool,
    sharedSceneTool('import_asset'),
    {
        tool: {
            name: 'save_as_asset',
            input_schema: {
                type: 'object' as const,
                properties: {
                    name: { type: 'string' },
                    object_names: {
                        type: 'array',
                        items: { type: 'string' },
                    },
                    replace_with_reference: { type: 'boolean' },
                },
                required: ['name'],
            },
        },
        execute: executeSaveAsAsset,
    },
];
